<template>
  <div class="app-container">
    <Header />
    <div class="blog-content-container">
      <div :class="mainContentClass">
        <div class="blog-header">
          <span class="blog-date">{{ date }}</span>
          <h1 class="blog-title">{{ title }}</h1>
          <div class="blog-meta">
            <div class="author-info">
              <img
                :src="authorAvatar || '/blog_assets/logo.png'"
                class="author-avatar"
              />
              <span class="author-name">{{ author || "Skyhost Team" }}</span>
            </div>
          </div>
        </div>

        <div class="climate-change-blog">
          <div class="blog-hero-image">
            <img :src="heroImage" alt="Blog hero image" class="hero-image" />
          </div>

          <section class="blog-section">
            <p>
              Climate change is reshaping our oceans in real time, and few
              places illustrate these shifts as vividly as the Sea of Cortés.
              Once celebrated as the "Aquarium of the World," this unique marine
              corridor now faces rising temperatures, acidification, and habitat
              loss. In this post, we'll embark on a virtual tour with marine
              ecologist Regi Domingo, examining the latest scientific findings
              and on-the-ground conservation efforts that offer both hope and
              urgency for one of Mexico's most treasured ecosystems.
            </p>
          </section>

          <section class="blog-section">
            <h2>The Sea of Cortés: A Marine Jewel Under Threat</h2>
            <p>
              The Sea of Cortés, or Gulf of California, stretches along Mexico's
              Baja peninsula and supports over 800 fish species and 2,000
              invertebrate species—making it one of the planet's richest marine
              biodiversity hotspots
              <a
                href="https://www.worldwildlife.org/places/gulf-of-california"
                target="_blank"
                class="blog-link"
                >World Wildlife Fund</a
              >. Yet this abundance is jeopardized by a warming ocean: average
              sea surface temperatures here have risen sharply in recent
              decades, mirroring global trends of 0.19 °C per decade in nearby
              Gulf regions
              <a
                href="https://www.noaa.gov/ocean-climate-data"
                target="_blank"
                class="blog-link"
                >NOAA Ocean Climate Data</a
              >.
            </p>
            <p>
              In La Paz, the bay's vibrant waters now register summer highs near
              30 °C, pushing local species toward their thermal limits
              <a
                href="https://www.frontiersin.org/journals/marine-science"
                target="_blank"
                class="blog-link"
                >Frontiers in Marine Science</a
              >. Such warming not only stresses corals and plankton but also
              fuels more intense harmful algal blooms and extends hypoxic "dead
              zones," disrupting ecological balance and coastal economies
              <a
                href="https://www.sciencedirect.com/journal/marine-pollution-bulletin"
                target="_blank"
                class="blog-link"
                >Marine Pollution Bulletin</a
              >.
            </p>
          </section>

          <section class="blog-section">
            <h2>Rising Temperatures and Acidification</h2>
            <p>
              Ocean acidification—driven by increased carbon dioxide
              absorption—lowers pH levels, weakening coral skeletons and
              shellfish exoskeletons. Since the 1980s, acidification in the Gulf
              of California has intensified, contributing to the world's fourth
              global coral bleaching event in 2023, which affected 84% of reefs
              worldwide
              <a
                href="https://coralreefwatch.noaa.gov/"
                target="_blank"
                class="blog-link"
                >NOAA Coral Reef Watch</a
              >
              <a
                href="https://www.ipcc.ch/report/ar6/wg1/"
                target="_blank"
                class="blog-link"
                >IPCC Climate Report</a
              >.
            </p>
            <p>
              Under these conditions, early coral mortality and reduced
              calcification rates threaten the structural integrity of reefs
              that protect shorelines and sustain fisheries. Regi Domingo's team
              uses live drone footage to monitor bleaching hotspots in the Sea
              of Cortés, capturing shifts in coral coloration that signal stress
              long before mass die-offs occur.
            </p>
          </section>

          <section class="blog-section">
            <h2>Biodiversity in Flux: Species Impact</h2>
            <p>
              Warming waters and changing chemistry force many marine species to
              migrate or adapt. A recent NOAA Fisheries vulnerability assessment
              found that temperature, salinity, and acidification pose high
              exposure risks to 63% of fish and invertebrate species in adjacent
              regions
              <a
                href="https://www.fisheries.noaa.gov/feature-story/climate-vulnerability-assessment"
                target="_blank"
                class="blog-link"
                >NOAA Fisheries</a
              >. Sharks, rays, and certain shellfish are particularly
              vulnerable, while more resilient species like menhaden and shrimp
              may persist longer under stress.
            </p>
            <p>
              Drone surveys led by Domingo reveal altered schooling behavior in
              pelagic fish and shifting plankton blooms. These real-time
              observations, streamed directly into classrooms, allow students to
              witness how climate stressors ripple through the food web—from
              microscopic algae to top predators.
            </p>
          </section>

          <section class="blog-section">
            <h2>Conservation Efforts with Regi Domingo</h2>
            <p>
              Despite these challenges, dedicated conservation projects are
              making a difference. In San Lorenzo Marine Archipelago National
              Park, efforts to protect key nesting sites for brown pelicans and
              elegant terns help preserve avian biodiversity even as marine
              habitats change
              <a
                href="https://www.conanp.gob.mx/"
                target="_blank"
                class="blog-link"
                >CONANP - Mexican National Commission for Protected Natural
                Areas</a
              >.
            </p>
            <p>
              Domingo partners with local communities to deploy coral nurseries
              and establish no-take zones. Live drone tours highlight these
              areas, showing students how restoration frames support new coral
              growth and how protected reefs recover more quickly than
              unprotected ones.
            </p>
          </section>

          <section class="blog-section" style="border-bottom: none">
            <h2>Conclusion: A Call to Action</h2>
            <p>
              The live drone perspective over the Sea of Cortés brings home the
              reality of climate change—and the power of informed action. By
              integrating Domingo's footage into lessons, educators can connect
              students to pressing environmental issues and inspire stewardship.
              As global ocean temperatures continue to set new records, telling
              the story of this iconic gulf is more critical than ever
              <a
                href="https://www.climate.gov/news-features/understanding-climate/climate-change-global-sea-surface-temperature"
                target="_blank"
                class="blog-link"
                >Climate.gov</a
              >
              <a
                href="https://oceanservice.noaa.gov/hazards/ocean-warming/"
                target="_blank"
                class="blog-link"
                >NOAA Ocean Service</a
              >.
            </p>
            <p>
              For more on scheduling a live Skyhost Sustainability Tour and
              bringing these vital stories to your classroom, visit
              <a
                href="https://skyhost.ai/schools"
                target="_blank"
                class="blog-link"
                >skyhost.ai/schools</a
              >. Witness the changes, understand the science, and join the
              movement to protect our oceans—one flight at a time.
            </p>
          </section>
        </div>

        <div class="blog-footer">
          <div class="blog-tags" v-if="tags && tags.length">
            <span class="tag-label">Tags:</span>
            <span v-for="(tag, index) in tags" :key="index" class="blog-tag">
              {{ tag }}
            </span>
          </div>

          <div class="share-section">
            <span class="share-label">Share this article:</span>
            <div class="share-icons">
              <vue-feather
                type="twitter"
                size="18"
                class="share-icon"
                @click="shareOnTwitter"
              ></vue-feather>
              <vue-feather
                type="facebook"
                size="18"
                class="share-icon"
                @click="shareOnFacebook"
              ></vue-feather>
              <vue-feather
                type="linkedin"
                size="18"
                class="share-icon"
                @click="shareOnLinkedIn"
              ></vue-feather>
              <vue-feather
                type="mail"
                size="18"
                class="share-icon"
                @click="shareByEmail"
              ></vue-feather>
            </div>
          </div>

          <div class="related-posts" v-if="relatedBlogs.length">
            <h3 class="related-title">Related articles</h3>
            <div class="related-grid">
              <div
                v-for="relatedBlog in relatedBlogs"
                :key="relatedBlog.id"
                class="related-card"
                @click="navigateToBlog(relatedBlog.id)"
              >
                <img
                  :src="relatedBlog.image"
                  alt="Related blog image"
                  class="related-image"
                />
                <div class="related-content">
                  <h4 class="related-blog-title">{{ relatedBlog.title }}</h4>
                  <span class="related-date">{{ relatedBlog.date }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="back-to-blogs">
            <button class="blog-btn" @click="navigateToBlogs">
              <vue-feather
                type="arrow-left"
                size="16"
                class="back-icon"
              ></vue-feather>
              Back to all blogs
            </button>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import { blogsRegistry } from "@/data/blogRegistry";

export default {
  name: "ClimateChangeSeaCortesBlog",
  components: {
    Header,
    Footer,
  },
  data() {
    const blogId = "climate-change-sea-cortes";
    const blog = blogsRegistry[blogId];

    return {
      id: blogId,
      title: blog.title,
      date: blog.date,
      author: blog.author,
      authorAvatar: blog.authorAvatar,
      image: blog.image,
      heroImage: blog.image,
      excerpt: blog.excerpt,
      tags: blog.tags,
      relatedBlogs: [],
    };
  },
  computed: {
    isMobileLayout() {
      return isMobile();
    },
    currentPageUrl() {
      return `${window.location.origin}${this.$route.fullPath}`;
    },
  },
  methods: {
    navigateToBlog(blogId) {
      this.$router.push({
        path: `/blog/${blogId}`,
      });
    },
    shareOnTwitter() {
      const text = `${this.title} - Check out this article by Skyhost`;
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(this.currentPageUrl)}`;
      window.open(url, "_blank");
    },
    shareOnFacebook() {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareOnLinkedIn() {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareByEmail() {
      const subject = encodeURIComponent(this.title);
      const body = encodeURIComponent(
        `I thought you might find this article interesting: ${this.title}\n\n${this.currentPageUrl}`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    },
    navigateToBlogs() {
      this.$router.push({
        path: "/blogs",
      });
    },
  },
  created() {
    // Get related blogs (excluding current blog)
    this.relatedBlogs = Object.values(blogsRegistry)
      .filter((blog) => blog.id !== this.id)
      .slice(0, 2); // Limit to 2 related blogs
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-blog-content" : "web-blog-content";
    });
    return {
      mainContentClass,
    };
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  color: #333;
}

.blog-content-container {
  margin-top: 5rem;
}

.web-blog-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.mobile-blog-content {
  padding: 1.5rem;
}

.blog-header {
  margin-bottom: 2rem;
}

.blog-date {
  color: #666;
  font-size: 1rem;
}

.blog-title {
  font-size: 2.5rem;
  margin: 1rem 0;
  color: #000;
  line-height: 1.3;
}

.blog-meta {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
}

.author-info {
  display: flex;
  align-items: center;
}

.author-avatar {
  width: 20px;
  height: 20px;
  margin-right: 0.8rem;
  object-fit: contain;
}

.author-name {
  font-weight: 500;
  color: #333;
}

.climate-change-blog {
  font-family: "Inter", "Helvetica", sans-serif;
  line-height: 1.8;
  color: #333;
}

.blog-hero-image {
  margin-bottom: 2rem;
  border-radius: 8px;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  max-height: 500px;
  border-radius: 8px;
}

.blog-section {
  margin-bottom: 4rem;
  padding: 2rem 0;
  border-bottom: 1px solid #f0f0f0;
}

h2 {
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #000;
  font-weight: 600;
}

h3 {
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #000;
  font-weight: 500;
}

p {
  margin-bottom: 1.5rem;
  font-size: 1.05rem;
}

.blog-link {
  color: #1565c0;
  text-decoration: underline;
}

.blog-footer {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
}

.blog-tags {
  margin-bottom: 2rem;
}

.tag-label {
  font-weight: 500;
  margin-right: 0.5rem;
}

.blog-tag {
  display: inline-block;
  padding: 0.3rem 0.8rem;
  margin: 0.3rem;
  background-color: #f0f0f0;
  border-radius: 20px;
  font-size: 0.9rem;
  color: #666;
}

.share-section {
  display: flex;
  align-items: center;
  margin-bottom: 3rem;
}

.share-label {
  font-weight: 500;
  margin-right: 1rem;
}

.share-icons {
  display: flex;
  gap: 1rem;
}

.share-icon {
  cursor: pointer;
  color: #555;
}

.share-icon:hover {
  color: #1565c0;
}

.related-posts {
  margin-bottom: 3rem;
}

.related-title {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #000;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.related-card {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: transform 0.3s ease;
}

.related-card:hover {
  transform: translateY(-3px);
}

.related-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.related-content {
  padding: 1rem;
}

.related-blog-title {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #000;
}

.related-date {
  font-size: 0.8rem;
  color: #666;
}

.back-to-blogs {
  margin-top: 3rem;
  text-align: center;
}

.blog-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.8rem 1.5rem;
  background-color: #f0f0f0;
  border: none;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.blog-btn:hover {
  background-color: #e0e0e0;
}

.back-icon {
  margin-right: 0.5rem;
}

@media (max-width: 768px) {
  .blog-title {
    font-size: 2rem;
  }

  .related-grid {
    grid-template-columns: 1fr;
  }
}
</style>
